import { useState, useEffect, useRef } from "react";
import { C, uid, fmt, Btn, Input, Select, Textarea, Modal, StatCard, Tabs, useIsMobile } from "./ui";
import { publicacionService, contactoService } from "../services/supabase";

const TIPOS_PUB = ["Fardos", "Huevos", "Animal", "Lana", "Producción", "Otro"];
const ICON_PUB = { Fardos:"📦", Huevos:"🥚", Animal:"🐄", Lana:"🧶", "Producción":"🌾", Otro:"🏷️" };

// Genera un link a WhatsApp (asume Uruguay +598 si no trae código de país)
function waLink(phone, text) {
  let num = (phone || "").replace(/\D/g, "");
  if (!num) return null;
  if (!num.startsWith("598") && num.length <= 9) num = "598" + num;
  return `https://wa.me/${num}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
}

// Redimensiona y comprime una imagen a base64
function fileToResized(file, maxSize = 700, quality = 0.6) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) { height = Math.round(height * maxSize / width); width = maxSize; }
        else if (height > maxSize) { width = Math.round(width * maxSize / height); height = maxSize; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject; img.src = e.target.result;
    };
    reader.onerror = reject; reader.readAsDataURL(file);
  });
}

export function PublicacionesPanel({ user, profile, onIngreso }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("mercado");
  const [mercado, setMercado] = useState([]);
  const [mios, setMios] = useState([]);
  const [interesados, setInteresados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactar, setContactar] = useState(null); // publicación a contactar

  const recargar = async () => {
    setLoading(true);
    const [m, p, c] = await Promise.all([
      publicacionService.mercado(), publicacionService.mios(), contactoService.recibidos(),
    ]);
    setMercado(m); setMios(p); setInteresados(c); setLoading(false);
  };
  useEffect(() => { recargar(); }, []);

  const tabs = [
    { id: "mercado", label: `🛒 Mercado` },
    { id: "mios", label: `📢 Mis avisos` },
    { id: "interesados", label: `📨 Interesados${interesados.filter(c=>!c.leido).length?` (${interesados.filter(c=>!c.leido).length})`:""}` },
  ];

  return (
    <div style={{ flex: 1, overflowY: "auto", background: C.fondo }}>
      <div style={{ padding: isMobile ? "14px 14px" : "20px 24px", maxWidth: 940, margin: "0 auto" }}>
        <div style={{ fontFamily: "serif", fontSize: 22, color: C.tierra, fontWeight: 600, marginBottom: 4 }}>
          Mercado · Comprá y vendé
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
          Avisos de todos los productores. Publicá tus excedentes y contactá a otros — tu celular no se muestra público.
        </div>

        <Tabs tabs={tabs} active={tab} onChange={setTab} />

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: C.muted }}>Cargando…</div>
        ) : (
          <>
            {tab === "mercado"     && <Mercado items={mercado} user={user} onContactar={setContactar} />}
            {tab === "mios"        && <MisAvisos mios={mios} profile={profile} onIngreso={onIngreso} recargar={recargar} />}
            {tab === "interesados" && <Interesados items={interesados} recargar={recargar} />}
          </>
        )}

        {contactar && (
          <ModalContactar pub={contactar} profile={profile} onClose={() => setContactar(null)}
            onEnviado={() => { setContactar(null); }} />
        )}
      </div>
    </div>
  );
}

// ─── VISTA MERCADO (todos los avisos) ─────────────────────
function Mercado({ items, user, onContactar }) {
  if (items.length === 0)
    return <Vacio ic="🛒" txt="Todavía no hay avisos publicados. ¡Sé el primero en publicar!" />;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
      {items.map(p => {
        const propio = p.user_id === user?.id;
        return (
          <Card key={p.id} p={p}>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>📍 {p.campo_nombre || "Campo"}{p.ubicacion ? ` · ${p.ubicacion}` : ""}</div>
            {propio
              ? <div style={{ fontSize: 11, color: C.muted, marginTop: 8, fontStyle: "italic" }}>Tu aviso</div>
              : <Btn size="sm" variant="primary" onClick={() => onContactar(p)} style={{ width: "100%", marginTop: 10 }}>✉️ Contactar</Btn>}
          </Card>
        );
      })}
    </div>
  );
}

// ─── VISTA MIS AVISOS ─────────────────────────────────────
function MisAvisos({ mios, profile, onIngreso, recargar }) {
  const fileRef = useRef(null);
  const [show, setShow] = useState(false);
  const [tipo, setTipo] = useState(TIPOS_PUB[0]);
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDesc] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [precio, setPrecio] = useState("");
  const [foto, setFoto] = useState("");
  const [cargando, setCargando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);

  const elegirFoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCargando(true);
    try { setFoto(await fileToResized(file)); } catch { /* */ }
    setCargando(false);
  };

  const publicar = async () => {
    if (!titulo) return;
    await publicacionService.crear({
      campo_nombre: profile?.nombre_campo || "Mi campo", ubicacion: profile?.ubicacion || "",
      tipo, titulo, descripcion, cantidad, precio: parseFloat(precio) || 0, foto, estado: "activa",
    });
    setShow(false); setTitulo(""); setDesc(""); setCantidad(""); setPrecio(""); setFoto(""); setTipo(TIPOS_PUB[0]);
    recargar();
  };
  const vender = async (p) => {
    await publicacionService.actualizar(p.id, { estado: "vendida" });
    if (onIngreso && p.precio > 0) onIngreso({
      id: uid(), tipo: "ingreso", categoria: p.tipo === "Animal" ? "Venta de animales" : "Lana / Productos",
      concepto: `Venta: ${p.titulo}`, monto: p.precio, fecha: new Date().toISOString().slice(0, 10), obs: "Mercado",
    });
    recargar();
  };
  const borrar = async (id) => { await publicacionService.eliminar(id); setConfirmar(null); recargar(); };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <Btn variant="primary" onClick={() => setShow(v => !v)}>{show ? "Cancelar" : "+ Nuevo aviso"}</Btn>
      </div>

      {show && (
        <div style={{ background: C.white, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${C.arenaDark}` }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <Select label="Tipo" value={tipo} onChange={setTipo} options={TIPOS_PUB} />
            <Input label="Título" value={titulo} onChange={setTitulo} placeholder="Ej: 50 fardos de raigrás" />
            <Input label="Cantidad" value={cantidad} onChange={setCantidad} placeholder="Ej: 50 unidades" />
            <Input label="Precio total ($)" value={precio} onChange={setPrecio} type="number" placeholder="0" />
          </div>
          <Textarea label="Descripción" value={descripcion} onChange={setDesc} rows={2} placeholder="Detalles, estado, etc." />
          <div style={{ marginBottom: 14 }}>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={elegirFoto} style={{ display: "none" }} />
            {foto ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <img src={foto} alt="" style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 10, border: `1px solid ${C.arenaDark}` }} />
                <button onClick={() => setFoto("")} style={{ position: "absolute", top: 6, right: 6, background: "rgba(0,0,0,0.6)", color: "#fff", border: "none", borderRadius: "50%", width: 28, height: 28, cursor: "pointer" }}>×</button>
              </div>
            ) : (
              <Btn variant="secondary" onClick={() => fileRef.current?.click()} disabled={cargando}>{cargando ? "Procesando…" : "📷 Tomar / subir foto"}</Btn>
            )}
          </div>
          <Btn variant="primary" onClick={publicar} disabled={!titulo} style={{ width: "100%" }}>Publicar en el mercado</Btn>
        </div>
      )}

      {mios.length === 0 && !show && <Vacio ic="📢" txt="No publicaste nada todavía. Creá tu primer aviso." />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
        {mios.map(p => {
          const vendida = p.estado === "vendida";
          return (
            <Card key={p.id} p={p} dim={vendida}>
              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                {!vendida && <Btn size="sm" variant="primary" onClick={() => vender(p)} style={{ flex: 1 }}>✓ Vendí</Btn>}
                {confirmar === p.id ? (
                  <>
                    <Btn size="sm" variant="danger" onClick={() => borrar(p.id)} style={{ flex: 1 }}>Borrar</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setConfirmar(null)} style={{ flex: 1 }}>No</Btn>
                  </>
                ) : <Btn size="sm" variant="ghost" onClick={() => setConfirmar(p.id)}>🗑</Btn>}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── VISTA INTERESADOS (contactos recibidos) ──────────────
function Interesados({ items, recargar }) {
  if (items.length === 0) return <Vacio ic="📨" txt="Nadie te contactó todavía. Cuando alguien se interese en tu aviso, vas a ver su contacto acá." />;
  const marcar = async (id) => { await contactoService.marcarLeido(id); recargar(); };
  return (
    <div>
      {items.map(c => (
        <div key={c.id} onClick={() => !c.leido && marcar(c.id)} style={{
          background: c.leido ? C.white : "#FBF6EA", borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          border: `1px solid ${c.leido ? C.arenaDark : "#E8D9B0"}`, cursor: c.leido ? "default" : "pointer" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.tierra }}>{!c.leido && "🔵 "}Interés en: {c.publicacion_titulo}</div>
            <span style={{ fontSize: 10, color: C.muted, fontFamily: "monospace", whiteSpace: "nowrap" }}>{fmt(c.created_at?.slice(0, 10))}</span>
          </div>
          {c.mensaje && <div style={{ fontSize: 12.5, color: C.texto, marginTop: 6, fontStyle: "italic" }}>“{c.mensaje}”</div>}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, color: C.tierra, fontWeight: 600 }}>
              👤 {c.de_campo || "Productor"}{c.de_celular ? ` · ${c.de_celular}` : ""}
            </div>
            {c.de_celular && waLink(c.de_celular) && (
              <a href={waLink(c.de_celular, `Hola! Te contacto por tu interés en "${c.publicacion_titulo}" en CampoIA.`)}
                target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none",
                  background: "#25D366", color: "#fff", borderRadius: 8, padding: "7px 13px", fontSize: 13, fontWeight: 700 }}>
                💬 Responder por WhatsApp
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── MODAL CONTACTAR ──────────────────────────────────────
function ModalContactar({ pub, profile, onClose, onEnviado }) {
  const [mensaje, setMensaje] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [ok, setOk] = useState(false);

  const enviar = async () => {
    setEnviando(true);
    try {
      await contactoService.enviar({
        publicacion_id: pub.id, publicacion_titulo: pub.titulo, para_user_id: pub.user_id,
        de_campo: profile?.nombre_campo || "Productor", de_celular: profile?.phone || "", mensaje,
      });
      setOk(true);
      setTimeout(() => { onEnviado(); }, 1100);
    } catch (e) { alert("No se pudo enviar: " + e.message); }
    setEnviando(false);
  };

  return (
    <Modal title={`Contactar · ${pub.titulo}`} onClose={onClose} width={440}>
      {ok ? (
        <div style={{ textAlign: "center", padding: "20px 10px" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 14, color: C.tierra, fontWeight: 600 }}>¡Interés enviado!</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{pub.campo_nombre} va a recibir tu contacto.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
            Le mandás tu interés a <b>{pub.campo_nombre || "el productor"}</b>. Recibirá tu nombre de campo y celular para responderte. Tu número no se publica.
          </div>
          <Textarea label="Mensaje (opcional)" value={mensaje} onChange={setMensaje} rows={3} placeholder="Hola, me interesa tu aviso…" />
          <Btn variant="primary" onClick={enviar} disabled={enviando} style={{ width: "100%" }}>
            {enviando ? "Enviando…" : "✉️ Enviar interés"}
          </Btn>
        </>
      )}
    </Modal>
  );
}

// ─── Tarjeta de aviso (reusable) ──────────────────────────
function Card({ p, dim, children }) {
  return (
    <div style={{ background: C.white, borderRadius: 12, overflow: "hidden", border: `1.5px solid ${C.arenaDark}`, opacity: dim ? 0.6 : 1, display: "flex", flexDirection: "column" }}>
      {p.foto
        ? <img src={p.foto} alt="" style={{ width: "100%", height: 150, objectFit: "cover" }} />
        : <div style={{ width: "100%", height: 150, background: C.arena, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 44 }}>{ICON_PUB[p.tipo] || "🏷️"}</div>}
      <div style={{ padding: "12px 14px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.tierra }}>{p.titulo}</div>
          {p.estado === "vendida" && <span style={{ fontSize: 10, background: C.arenaDark, color: C.tierra, borderRadius: 10, padding: "2px 8px", fontWeight: 700 }}>VENDIDA</span>}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{ICON_PUB[p.tipo]} {p.tipo}{p.cantidad ? ` · ${p.cantidad}` : ""}</div>
        {p.descripcion && <div style={{ fontSize: 12, color: C.texto, marginTop: 6, lineHeight: 1.4 }}>{p.descripcion}</div>}
        {p.precio > 0 && <div style={{ fontFamily: "serif", fontSize: 20, fontWeight: 700, color: C.pasto, marginTop: 8 }}>${p.precio.toLocaleString("es-UY")}</div>}
        <div style={{ marginTop: "auto" }}>{children}</div>
      </div>
    </div>
  );
}

function Vacio({ ic, txt }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: C.muted }}>
      <div style={{ fontSize: 36, marginBottom: 10 }}>{ic}</div>
      <div style={{ fontSize: 13 }}>{txt}</div>
    </div>
  );
}
